/**
 * Connect App Global Tool
 *
 * V1 in-chat one-click connect card
 * (PLAN-storage-folder-permissions-strategy-20260811.md §7.3).
 *
 * When a user asks to connect an app — or asks for an action on an app that
 * is not connected yet — this tool resolves the request against the live
 * Composio catalog and posts a connect CARD in the chat: one element per
 * matched app with a "connect <App>" button. Clicking the button mints the
 * OAuth URL AT CLICK TIME (connection requests expire, so the URL is never
 * pre-minted into the card), opens the popup exactly like Onboarding's
 * `connectAppInFlow`, and polls `getConnectedAccounts()` until the account
 * reports ACTIVE.
 *
 * Button mechanism
 * ----------------
 * The connect buttons ride `workflowActionButtonsHtml` (injected raw in
 * SpecialMessageTile, so `data-*` attributes SURVIVE — plain message `html`
 * goes through normalizeChatHtml which strips them). The button id prefix is
 * registered in this tool's `buttonIds` so `findToolByButtonId` can map a
 * click back to the tool, but the click itself is handled inline in
 * `useGlobalTools.handleButtonClick` (browser-use pattern) via the exported
 * `launchConnectFlow` helper — the generic registry path would restart the
 * tool and re-post the offer card, and the click needs button-DOM loading
 * state that only the hook has access to.
 *
 * Structured events (payloads carry DATA ONLY — the post-tool renderer
 * composes all user-facing prose):
 *   - composio.connect_offer              (card posted; buttons ride along)
 *   - composio.connect_unavailable        (requested app not in catalog)
 *   - composio.already_connected          (requested app already ACTIVE)
 *   - composio.connection_complete        (poll observed ACTIVE account)
 *   - composio.connection_timeout         (poll gave up; popup may be open)
 *   - composio.connect_permission_denied  (server enforces integrations.manage)
 *   - composio.connect_error              (any other connect failure)
 */

import {
  listToolkits,
  getConnectedAccounts,
  fetchConnectResearch,
} from '../../composio/ComposioClient';
import type { ComposioToolkit, ConnectResearch, ConnectResearchMethod } from '../../composio/ComposioClient';
import { searchToolkitsForResolver, firstActionForApp } from '../../composio/appSearch';
import { connectLaneFor, laneIsDirect, toolkitDeclaresKeyScheme } from '../../composio/connectLane';
// The sign-in host rules (contract C5, 2026-09-02): imported, never re-typed
// here — tests/onboarding/signinHostRulesParity.test.ts pins that this file
// carries no inline copy of the auth-host / search-engine regexes.
import { isSearchEngineHost } from '../../composio/signinHosts';
import { friendlyIntegrationName } from '../../composio/integrationMeta';
import {
  runConnectPopupFlow,
  CONNECT_POLL_INTERVAL_MS,
  CONNECT_POLL_MAX_TICKS,
  logConnectFunnel,
} from '../../composio/connectPopupFlow';
import { deriveMicrosoftAdminConsentUrl, isEmailJourneySlug } from '../../composio/providerJourneyMeta';
import {
  buildAdminHandoffNote,
  deriveGoogleOAuthClientId,
  providerForEmailToolkitSlug,
} from '../../composio/adminHandoff';
import { isAgenticWorkflowsAuthorized } from '../../agenticWorkflows';
import { detectInputLanguage } from '../../agenticWorkflows/languageMirror';
import { makeRegexTriggerWithExtractor } from '../triggerHelpers';
import { getPendingWidgetHtml } from '../widgetHtml';
import {
  escapeHtml,
  safeHttpsUrl,
  renderCardShell,
  renderPillButton,
  renderLinkPill,
  renderChoiceDisclosure,
} from './actionCardKit';
import type {
  GlobalToolDefinition,
  GlobalToolId,
  MessagingHelpers,
  StepContext,
  StepResult,
  ToolTheme,
} from '../types';

// ============================================================================
// Constants
// ============================================================================

/**
 * imported everywhere else (registry entry, tests) to keep one source.
 */
export const CONNECT_APP_TOOL_ID: GlobalToolId = 'connect-app';

// Prest0 brand palette (2026-08-12 owner directive: every chat element rides
// the platform scheme — warm ink + gold, no one-off hues). Buttons follow the
// onboarding convention: black pill, lowercase.
const PREST0_INK = '#1C1917';
const PREST0_GOLD = 'var(--brand-gold)';
const PREST0_GOLD_TINT = 'rgba(198, 167, 94, 0.12)';
const PREST0_WARM_BORDER = '#E5E0D5';

const CONNECT_APP_THEME: ToolTheme = {
  accent: PREST0_INK,       // ink — the platform CTA color (onboarding pills)
  accentBg: '#FFFFFF',      // cards sit on white, bordered — never a tinted wash
  border: PREST0_WARM_BORDER,
  subtitle: '#6B6459',      // warm muted ink
  muted: '#857F73',
};

/**
 * Button id PREFIX for the connect card. Actual rendered buttons are
 * `${prefix}-${toolkitSlug}` — `findToolByButtonId` prefix-matches, and the
 * inline handler in useGlobalTools matches on `startsWith(prefix)`.
 */
export const CONNECT_APP_BUTTON_IDS = {
  connect: 'global-tool-connect-app-btn',
  /** Browser-lane fallback (2026-08-11): "open <app> on Preston's computer"
   * when the catalog has no one-click toolkit. Click handler lives in
   * useGlobalTools and starts the browser-use tool with the sign-in goal. */
  browser: 'global-tool-connect-app-browser-btn',
  /** Popup-blocked fallback (2026-08-19): re-open the minted OAuth URL from
   * a DIRECT user click (same shared window name → never popup-blocked).
   * The original poll keeps running, so the click lands in the same
   * completion path. Handler in useGlobalTools. */
  reopen: 'global-tool-connect-app-reopen-btn',
  /** Key lane (2026-09-03): the "other ways" api-key row becomes ACTIONABLE
   * for a toolkit whose catalog entry declares a key scheme. Handler in
   * useGlobalTools: it asks the server what the key mint needs and opens the
   * key dialog, or falls back to the browser lane when the server says this
   * app has no key sign-in after all. */
  key: 'global-tool-connect-app-key-btn',
  /** Mismatch/no-mailbox remedy (C3b, 2026-08-21): remove the wrong sign-in
   * and relaunch with the SAME intended address. Handler in useGlobalTools —
   * disconnect the carried connection id, then launchConnectFlow with the
   * carried intent (no re-prompt). */
  retryAs: 'global-tool-connect-app-retry-as-btn',
  /** Alternative pills inside the connect card's "other ways ▾" disclosure
   * (plan §9.5a, 2026-09-02). P0 renders alternatives as informational
   * ANCHORS (never a dead <button>); the family is reserved and exempt from
   * CTA retirement from day one so P1a's real pills stay live. */
  alt: 'global-tool-connect-app-alt-btn',
  /** Question-option pills on the P1a question card (plan §9.4a) — retired
   * and ✓-painted from the intent RECORD, never from message order. Unused
   * in P0; regex-reserved. */
  answer: 'global-tool-connect-app-answer-btn',
};

/** Poll cadence — re-exported from the shared engine (one source, 3s × 40). */
export { CONNECT_POLL_INTERVAL_MS, CONNECT_POLL_MAX_TICKS };

// ============================================================================
// Trigger
// ============================================================================

/**
 * Trigger patterns for connect intent. Anchored to the start of the message
 * (composioAction.ts style) so they only fire on direct command-style
 * triggers; `makeRegexTriggerWithExtractor` adds the negation guard.
 * Group 1 captures the requested app phrase.
 */
const CONNECT_TRIGGER_PATTERNS: RegExp[] = [
  // "connect gmail", "connect my google drive", "can you connect to slack"
  /^(?:please\s+|can\s+you\s+|could\s+you\s+)?connect\s+(?:to\s+|with\s+)?(?:my\s+|the\s+)?([a-z0-9][\w .&-]{0,40})/i,
  // "link my slack", "integrate notion"
  /^(?:please\s+)?(?:link|integrate)\s+(?:to\s+|with\s+)?(?:my\s+|the\s+)?([a-z0-9][\w .&-]{0,40})/i,
  // "set up the notion integration", "enable gmail app", "add dropbox connection"
  /^(?:set\s+up|enable|add|activate)\s+(?:the\s+|my\s+)?([a-z0-9][\w .&-]{0,40}?)\s+(?:integration|connection|app|account)\b/i,
  // Spanish: "conecta mi gmail", "conectar google drive"
  /^(?:por\s+favor\s+)?conecta(?:r|me)?\s+(?:con\s+)?(?:mi\s+|el\s+|la\s+)?([a-z0-9][\w .&-]{0,40})/i,
];

/**
 * Phrases that indicate the user wants a human / non-app connection —
 * "connect me with a lawyer" must not produce an app-catalog miss reply.
 */
const NON_APP_PHRASES =
  /\b(lawyer|attorney|abogad[oa]s?|human|person|people|someone|somebody|agent|representative|support|team|dots|wifi|wi-fi|internet|vpn|printer|bluetooth)\b/i;

/** Trailing filler stripped off the captured app phrase. */
const APP_QUERY_TRAILERS =
  /\s+(?:account|app|application|integration|connection|workspace|for\s+me|please|now|cuenta|aplicaci[oó]n|por\s+favor)\.?\s*$/i;

function cleanAppQuery(raw: string): string {
  let out = (raw || '').trim().replace(/[?!.]+$/, '').trim();
  // Strip trailing filler repeatedly ("gmail account please").
  for (let i = 0; i < 3; i++) {
    const next = out.replace(APP_QUERY_TRAILERS, '').trim();
    if (next === out) break;
    out = next;
  }
  return out;
}

const baseConnectTrigger = makeRegexTriggerWithExtractor(
  CONNECT_TRIGGER_PATTERNS,
  (text, m) => ({
    appQuery: cleanAppQuery(m[1] || ''),
    query: text,
    matchedPrefix: m[0] || '',
  })
);

function matchConnectAppTrigger(text: string): {
  matches: boolean;
  appQuery?: string;
  query?: string;
} {
  const result = baseConnectTrigger(text);
  if (!result.matches) return { matches: false };
  const appQuery = (result as { appQuery?: string }).appQuery || '';
  if (!appQuery || NON_APP_PHRASES.test(appQuery)) return { matches: false };
  // COMPOUND ASKS FALL THROUGH to the planner (owner repro 2026-08-20:
  // "Connect Ring Central for me — find the best way to integrate it and
  // walk me through it" pre-routed CLIENT-ONLY, wrote no server turn
  // record, and silently dropped everything after the dash — the next
  // message then ran context-blind). A plain command still pre-routes
  // (only the client can mint the OAuth popup — 2026-08-12 rationale);
  // an ask carrying further instructions beyond the connect phrase goes
  // to the planner, which owns the whole request and still offers the card.
  const matchedPrefix = (result as { matchedPrefix?: string }).matchedPrefix || '';
  const rawTail = text.trim().slice(matchedPrefix.length);
  const residue = rawTail.replace(/^[\s,.;:!?—–-]+/, '');
  const residueWords = residue.split(/\s+/).filter(Boolean);
  // Instruction-shaped tails ("find the best way…", "and then summarize…")
  // carry further work; benefit blurbs from the suggestion chips ("— I can
  // triage email for you") do not and keep the pre-route.
  const residueIsInstruction =
    /^(?:and|then|also|after(?:wards)?|plus|find|help|walk|show|tell|explain|set\s?up|configure|research|summariz|review|draft|create|check)/i.test(residue);
  // Dash-led tails are the suggestion chips' own "— benefit" format (owner
  // incident 2026-08-20: "Connect my Google Drive — so you can read,
  // summarize, and edit the documents I keep there" — the plain length veto
  // sent OUR OWN chip clicks to the planner). A dash/colon-separated tail
  // that is not instruction-shaped is a benefit blurb at ANY length;
  // instruction-shaped tails still veto regardless of the separator.
  const dashLedBenefit = /^\s*[—–:-]/.test(rawTail) && !residueIsInstruction;
  if ((residueIsInstruction && residueWords.length > 1) || (!dashLedBenefit && residueWords.length > 8)) {
    return { matches: false };
  }
  // The capture class admits spaces, so trailing prose can be SWALLOWED
  // into the app name ("gmail and then summarize my unread emails…") —
  // no real app name runs past four words; longer means the ask carries
  // an instruction and belongs to the planner.
  if (appQuery.split(/\s+/).filter(Boolean).length > 4) return { matches: false };
  return result;
}

// (The 2026-08-12 backend-tool→toolkit-slug inference table and its export
// were deleted 2026-08-19: the client no longer infers an app from a failed
// tool's name — it hardcoded gmail for every email tool, which is how an
// Outlook-only firm was offered a Gmail card. Connect offers are
// orchestrator-decided (offer_app_connection).)

// ============================================================================
// Catalog resolution
// ============================================================================

/** A catalog app resolved for the connect offer. */
export interface ResolvedConnectApp {
  slug: string;
  name: string;
  description: string;
  oneClick: boolean;
  /** The toolkit's real logo URL (same asset the Integrations page renders)
   *  — the card shows the actual app icon, per the 2026-08-12 consistency
   *  directive. Absent → gold monogram fallback. */
  logo?: string;
}

// Intent keyword → toolkit slug map: moved to the shared search engine
// (src/services/composio/appSearch.ts SEARCH_SYNONYMS, 2026-08-18) so the
// Integrations page omnisearch, the connect-any-system lane, and this
// resolver all speak the same vocabulary.

// Friendly names live in the SHARED map (integrationMeta.ts, 2026-08-19) so
// this tool and the onboarding funnel never drift on app naming.
const friendlyToolkitName = friendlyIntegrationName;

// ============================================================================
// Browser-lane sign-in URLs (any-system rule, 2026-08-13)
// ============================================================================

/**
 * Known sign-in pages for the browser lane. The browser-use tool extracts a
 * URL from the START of its goal text (`/^(?:go to |open |visit )?(https?:…)/`)
 * and falls back to bing.com when there is none — which is exactly what the
 * old goal "go to the <app> sign-in page" produced (2026-08-13 bug: every
 * catalog-miss sign-in landed on Bing's homepage). The goal must therefore
 * EMBED a real URL. Keys are matched as substrings of the lowercased app
 * name, most-specific first.
 */
const SIGN_IN_URL_MAP: Array<[pattern: RegExp, url: string]> = [
  // Microsoft family — the incident case ("connect a Microsoft account").
  [/\boutlook\b/, 'https://outlook.live.com/'],
  [/\b(office\s*365|microsoft\s*365|onedrive|sharepoint|teams)\b/, 'https://login.microsoftonline.com/'],
  [/\bmicrosoft\b/, 'https://login.live.com/'],
  // Legal / practice systems
  [/\bwestlaw\b/, 'https://signon.thomsonreuters.com/'],
  [/\blexis(nexis)?\b/, 'https://signin.lexisnexis.com/'],
  [/\bclio\b/, 'https://account.clio.com/'],
  [/\bmycase\b/, 'https://www.mycase.com/login/'],
  [/\bpractice\s*panther\b/, 'https://app.practicepanther.com/login'],
  [/\bfilevine\b/, 'https://app.filevine.com/'],
  [/\bpacer\b/, 'https://pacer.login.uscourts.gov/'],
  [/\buscis\b/, 'https://myaccount.uscis.gov/'],
  [/\bdocusign\b/, 'https://account.docusign.com/'],
  // Common accounts
  [/\b(icloud|apple)\b/, 'https://www.icloud.com/'],
  [/\byahoo\b/, 'https://login.yahoo.com/'],
  [/\bzoom\b/, 'https://zoom.us/signin'],
  [/\blinked\s*in\b/, 'https://www.linkedin.com/login'],
  [/\bquick\s*books\b/, 'https://accounts.intuit.com/'],
  [/\bspotify\b/, 'https://accounts.spotify.com/login'],
  [/\bcanva\b/, 'https://www.canva.com/login'],
  [/\bshopify\b/, 'https://accounts.shopify.com/'],
  [/\bsquarespace\b/, 'https://login.squarespace.com/'],
  [/\bgodaddy\b/, 'https://sso.godaddy.com/'],
];

/**
 * Researched sign-in URLs (2026-08-18 connect concierge): the research route
 * returns the OFFICIAL https sign-in URL for systems outside the static map.
 * Registered here so every sign-in surface (VM desktop session, deep link,
 * computer-use pilot) lands on the real page instead of the search-results
 * fallback. Session-scoped; the server cache repopulates it cheaply.
 */
const RESEARCHED_SIGN_IN_URLS = new Map<string, string>();

/**
 * THE match key for "is this the same app?" comparisons — strips everything
 * but letters/digits so "Ring Central", "RingCentral" and "ring-central"
 * all collapse to `ringcentral` (2026-08-20 incident: the spelling drift
 * produced a duplicate connect card AND a cache-missed degraded research
 * verdict). MATCH KEY ONLY — never a persisted slug: `customSlugForName`
 * (desktopSignIn.ts) stays byte-synced with the server's slug derivation
 * and must not change.
 */
export function connectAppOfferKey(name: string): string {
  return String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Client mirror of the VM's search-engine host guard (signin-classify.ts):
 *  a search page is NEVER a sign-in destination — search engines hand
 *  automated browsers bot-check captchas (2026-08-20 Fitbit/Bing repro). */
export function isSearchEngineSignInUrl(url: string): boolean {
  // Auth surfaces on those domains are real sign-in destinations, not search
  // engines — accounts.google.com IS the login page for every Google-SSO app
  // (2026-08-22 Fitbit incident: Fitbit's real sign-in is Google SSO and this
  // guard refused it). The rule lives in signinHosts.ts (the client twin of
  // the VM's signin-classify.ts, byte-parity pinned); only a parseable URL is
  // judged, exactly like the VM's isSearchEngineUrl.
  try {
    new URL(url);
  } catch {
    return false;
  }
  return isSearchEngineHost(url);
}

export function registerResearchedSignInUrl(appName: string, url: string): void {
  const key = connectAppOfferKey(appName);
  const trimmed = (url || '').trim();
  // https only, NEVER a search engine, and NEVER embedded credentials
  // (2026-09-02 review): research is model output, and this registry feeds
  // desktopSignIn.ts's real browser-use navigation goal — a
  // https://user:pass@host/ URL hides its true host behind the userinfo and
  // would be navigated to for real. safeHttpsUrl is the same gate the card
  // renders through, so both paths refuse the same strings.
  if (key && safeHttpsUrl(trimmed) && !isSearchEngineSignInUrl(trimmed)) {
    RESEARCHED_SIGN_IN_URLS.set(key, trimmed);
  }
}

/**
 * The KNOWN sign-in URL for an app — static map first, then the researched
 * registry. Null when neither knows it, so callers can consult the server's
 * connect-research brain (the primary plane) before falling back.
 */
export function knownSignInUrlForApp(appName: string): string | null {
  const name = (appName || '').trim().toLowerCase();
  for (const [pattern, url] of SIGN_IN_URL_MAP) {
    if (pattern.test(name)) return url;
  }
  return RESEARCHED_SIGN_IN_URLS.get(connectAppOfferKey(appName)) || null;
}

/**
 * Resolve a sign-in URL for an arbitrary app/system name, never empty.
 * Unknown systems get the provider-domain guess (https://www.<key>.com) —
 * NEVER a search-engine URL (owner repros 2026-08-20: search engines hand
 * automated browsers bot-check captchas, and a search-results page parked
 * the Fitbit sign-in on a Bing challenge; the Kalshi session-start lane had
 * kept this fallback after the pilot lost it). A wrong domain guess is a
 * page the pilot/user can navigate away from; a captcha wall is a dead end.
 */
export function signInUrlForApp(appName: string): string {
  const known = knownSignInUrlForApp(appName);
  if (known) return known;
  // appName is validated non-empty at every call site; the empty-key case
  // is a neutral placeholder, deliberately NOT a search engine.
  const key = connectAppOfferKey(appName);
  return `https://www.${key || 'example'}.com`;
}

/**
 * Build the browser-use goal for the sign-in lane. The URL must be the first
 * token after "go to " so browserUse's URL extraction opens the right page.
 */
export function buildSignInBrowserGoal(appName: string): string {
  const name = (appName || '').trim() || 'the app';
  return `go to ${signInUrlForApp(name)} and help the user sign in to ${name}`;
}

/** What connecting this app lets Preston DO for the user — feeds the
 * situational one-liner the model writes above the connect card. */
const APP_PURPOSE_MAP: Record<string, string> = {
  gmail: 'read, triage, and send their email',
  outlook: 'read, triage, and send their email',
  googlecalendar: 'manage their schedule, events, and availability',
  calendly: 'manage their scheduling links and bookings',
  googledrive: 'find and work with their files and documents',
  googledocs: 'work with their documents',
  googlesheets: 'work with their spreadsheets',
  dropbox: 'find and work with their files',
  box: 'find and work with their files',
  one_drive: 'find and work with their files',
  onedrive: 'find and work with their files',
  googlecontacts: 'look up and manage their contacts',
  slack: 'work with their team messages',
  notion: 'work with their notes and wikis',
  hubspot: 'work with their CRM records',
  salesforce: 'work with their CRM records',
  quickbooks: 'work with their bookkeeping',
  stripe: 'look into their payments and invoices',
  youtube: 'manage and update the videos on their channel',
};

export function appPurposePhrase(slug: string): string {
  return APP_PURPOSE_MAP[slug.toLowerCase()] || 'work with it on their behalf';
}

function toResolvedApp(tk: ComposioToolkit): ResolvedConnectApp {
  // FULL description — the old 117-char slice cut mid-word ("…other G S...")
  // and read as broken UI (owner report, 2026-08-12). Catalog descriptions
  // are a sentence or two; the card fits them.
  const description = (tk.meta?.description || '').trim();
  return {
    slug: tk.slug,
    name: tk.name || friendlyToolkitName(tk.slug),
    description,
    // Honest flag (2026-08-17): true only when the button really is one
    // click — managed auth AND no typed setup values first (WhatsApp needs
    // a WABA ID before its sign-in can start).
    oneClick: connectLaneFor(tk) === 'oauth',
    logo: tk.meta?.logo || undefined,
  };
}

/**
 * Resolve a requested app phrase (or a whole action query) against the
 * Composio catalog. Delegates to the shared scored matcher (appSearch.ts,
 * 2026-08-18) — exact/synonym hits are authoritative; prefix/substring/typo
 * tiers rescue near-misses when nothing better matched. One-click OAuth
 * toolkits sort first. Capped at 3 so the card stays a card, not a directory.
 *
 * DIRECT LANES ONLY (2026-09-02). A catalog NAME match is not a connectable
 * app: only ~122 of the ~1,448 toolkits carry Composio-managed auth, and
 * "Google Chat" — the owner's own repro — is one of the ~1,326 that do not.
 * Offering its connect button minted nothing and left an error where a
 * sign-in window had just been. Non-direct matches are dropped here so the
 * callers' existing "no direct match" branch posts the researched
 * browser-lane card instead — the card that actually connects the app.
 * `browserLaneMatches` recovers what was dropped, so that card can use the
 * catalog's proper display name rather than whatever the user typed.
 */
export function resolveRequestedApps(
  requestedApp: string,
  toolkits: ComposioToolkit[]
): ResolvedConnectApp[] {
  return searchToolkitsForResolver(requestedApp, toolkits, 3)
    .filter((entry) => laneIsDirect(connectLaneFor(entry.tk)))
    .map((entry) => toResolvedApp(entry.tk));
}

/**
 * The catalog matches that resolveRequestedApps dropped because they have no
 * one-click sign-in — best match first. Used only to name the browser-lane
 * card ("Google Chat", not "google chat pls").
 */
export function browserLaneMatches(
  requestedApp: string,
  toolkits: ComposioToolkit[]
): ComposioToolkit[] {
  return searchToolkitsForResolver(requestedApp, toolkits, 3)
    .filter((entry) => connectLaneFor(entry.tk) === 'browser')
    .map((entry) => entry.tk);
}

/** The display name to put on a browser-lane card for this query: the
 *  catalog's own name when a toolkit matched, else the user's words. */
export function browserLaneDisplayName(
  requestedApp: string,
  toolkits: ComposioToolkit[]
): string {
  const hit = browserLaneMatches(requestedApp, toolkits)[0];
  return hit?.name || hit?.slug || requestedApp;
}

// ============================================================================
// HTML builders (ride workflowActionButtonsHtml — injected raw, data-* survive)
// ============================================================================

// escapeHtml is the kit's (one implementation, 2026-09-02).

/**
 * The connect card: one element per app — name, one-line description, and a
 * "connect <App>" button carrying `data-button-id`-compatible id plus
 * `data-toolkit-slug` / `data-toolkit-name` for the click handler.
 */
export function buildConnectOfferCardHtml(apps: ResolvedConnectApp[]): string {
  const items = apps
    .map((app) => {
      const slug = escapeHtml(app.slug);
      const name = escapeHtml(app.name);
      const description = escapeHtml(app.description);
      const buttonId = `${CONNECT_APP_BUTTON_IDS.connect}-${slug}`;
      // Real app icon — the SAME asset the Integrations page renders — with a
      // gold monogram fallback when the catalog has no logo.
      const icon = app.logo
        ? `<img src="${escapeHtml(app.logo)}" alt="" width="28" height="28" style="width:28px;height:28px;border-radius:7px;border:1px solid ${PREST0_WARM_BORDER};background:#fff;object-fit:contain;flex:none;" />`
        : `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:7px;background:${PREST0_GOLD_TINT};border:1px solid ${PREST0_GOLD};color:${PREST0_INK};font-weight:700;font-size:0.85rem;flex:none;">${name.slice(0, 1).toUpperCase()}</span>`;
      // Honest setup-first hint (2026-08-24 WhatsApp repro): the oneClick
      // flag already rode the payload — now the CARD says it too, so the
      // button never reads as one-click for an app that will ask for a
      // setup detail before its sign-in can start.
      const setupHint = app.oneClick === false
        ? `\n  <div style="color:${CONNECT_APP_THEME.subtitle};font-size:0.75rem;margin-top:6px;">asks for a quick setup detail before sign-in</div>`
        : '';
      return `<div style="border:1px solid ${CONNECT_APP_THEME.border};background:${CONNECT_APP_THEME.accentBg};border-radius:12px;padding:14px 16px;">
  <div style="display:flex;align-items:center;gap:10px;">${icon}<span style="font-weight:700;color:${PREST0_INK};font-size:0.95rem;">${name}</span></div>${
        description
          ? `\n  <div style="color:${CONNECT_APP_THEME.subtitle};font-size:0.82rem;margin-top:8px;line-height:1.5;">${description}</div>`
          : ''
      }${setupHint}
  <button id="${buttonId}" data-button-id="${buttonId}" data-toolkit-slug="${slug}" data-toolkit-name="${name}" style="margin-top:12px;padding:9px 18px;background:${PREST0_INK};color:#fff;border:none;border-radius:999px;font-weight:700;cursor:pointer;font-size:0.82rem;text-transform:lowercase;letter-spacing:0.01em;">connect ${name}</button>
</div>`;
    })
    .join('\n');

  return `<div style="margin-top:16px;display:flex;flex-direction:column;gap:10px;max-width:420px;">\n${items}\n</div>`;
}

/**
 * Add-another-account card for an ALREADY-CONNECTED app (2026-08-12): same
 * connect button id/flow — every OAuth run mints its own connection, so
 * "add another" is just connecting again; multi-account execution and
 * per-connection folder scopes handle the rest.
 */
export function buildAddAnotherCardHtml(app: ResolvedConnectApp): string {
  const slug = escapeHtml(app.slug);
  const name = escapeHtml(app.name);
  const buttonId = `${CONNECT_APP_BUTTON_IDS.connect}-${slug}`;
  const icon = app.logo
    ? `<img src="${escapeHtml(app.logo)}" alt="" width="28" height="28" style="width:28px;height:28px;border-radius:7px;border:1px solid ${PREST0_WARM_BORDER};background:#fff;object-fit:contain;flex:none;" />`
    : `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:7px;background:${PREST0_GOLD_TINT};border:1px solid ${PREST0_GOLD};color:${PREST0_INK};font-weight:700;font-size:0.85rem;flex:none;">${name.slice(0, 1).toUpperCase()}</span>`;
  return `<div style="margin-top:16px;border:1px solid ${CONNECT_APP_THEME.border};background:${CONNECT_APP_THEME.accentBg};border-radius:12px;padding:14px 16px;max-width:420px;">
  <div style="display:flex;align-items:center;gap:10px;">${icon}<span style="font-weight:700;color:${PREST0_INK};font-size:0.95rem;">${name}</span><span style="margin-left:auto;font-size:0.74rem;font-weight:600;color:${PREST0_GOLD};border:1px solid ${PREST0_GOLD};border-radius:999px;padding:2px 8px;">connected</span></div>
  <button id="${buttonId}" data-button-id="${buttonId}" data-toolkit-slug="${slug}" data-toolkit-name="${name}" style="margin-top:12px;padding:9px 18px;background:${PREST0_INK};color:#fff;border:none;border-radius:999px;font-weight:700;cursor:pointer;font-size:0.82rem;text-transform:lowercase;letter-spacing:0.01em;">add another ${name} account</button>
</div>`;
}

export type ConnectCardLang = 'en' | 'es';

/**
 * D9 inventory for the connect card (2026-09-02, plan §9.16 / §3.3). Code
 * authors ONLY chrome labels and failure/limit lines here — never content:
 * the recommendation line and every "other ways" eyebrow are model-authored
 * research the card projects verbatim. Every line is {en, es} (selected by
 * the thread's language, the clarifying-footer idiom); house lowercase with
 * "Prest0n" capitalized — legal on a button ONLY because the kit's
 * `.p0ac-card button{text-transform:none !important}` beats global.css's
 * lowercase rule by specificity (§3.3).
 */
export const CONNECT_CARD_COPY = {
  title: {
    en: "sign in on Prest0n's computer",
    es: 'inicia sesión en la computadora de Prest0n',
  },
  /** Destination truth: a probe-verified login page (or a bot-walled one the
   *  human will pass) — the pilot lands ON the sign-in form. */
  primarySignIn: {
    en: (name: string) => `open ${name} sign-in`,
    es: (name: string) => `abrir el inicio de sesión de ${name}`,
  },
  /** An https destination that is NOT a verified login page (the app's
   *  host, or an unprobed / unverified page): honest — we navigate there and
   *  the pilot finds the sign-in link. No extra line. */
  primaryApp: {
    en: (name: string) => `open ${name}`,
    es: (name: string) => `abrir ${name}`,
  },
  /** No destination at all. */
  primaryUnverified: {
    en: "open Prest0n's computer",
    es: 'abrir la computadora de Prest0n',
  },
  unverifiedNote: {
    en: "couldn't verify the sign-in page — type the address in the browser",
    es: 'no pude verificar la página de inicio de sesión — escribe la dirección en el navegador',
  },
  mobileLimit: {
    en: "this app is phone-only — the web account is the way in until Prest0n's phone arrives",
    es: 'esta app es solo para teléfono — la cuenta web es la entrada hasta que llegue el teléfono de Prest0n',
  },
  otherWays: {
    en: (n: number) => `other ways (${n})`,
    es: (n: number) => `otras formas (${n})`,
  },
  reconnect: {
    en: (name: string) => `sign in to ${name} again`,
    es: (name: string) => `volver a iniciar sesión en ${name}`,
  },
  /** The key lane's pill, beside the research's own "where to get one" link. */
  pasteKey: {
    en: 'paste your key',
    es: 'pega tu clave',
  },
} as const;

/** The https sign-in destination the research carries, or '' — never a
 *  guess (the fabricated `https://www.<key>.com` rung is C8's to delete);
 *  safeHttpsUrl (no userinfo) since the 2026-09-02 review. */
function researchSignInUrl(research?: ConnectResearch | null): string {
  return safeHttpsUrl(research?.signInUrl, 500);
}

/**
 * The server's custom-connection slug shape (`custom-<[a-z0-9_]{1,50}>`,
 * the register path in functions/src/composio/index.ts). Twin of the
 * literal in src/services/composio/connectCustomFlow.ts — pinned together
 * by tests/onboarding/connectReconnectNoRegister.test.ts.
 */
export const CUSTOM_CONNECTION_SLUG_RE = /^custom-[a-z0-9_]{1,50}$/;

function customConnectionSlug(raw: unknown): string {
  const slug = String(raw || '').trim();
  return CUSTOM_CONNECTION_SLUG_RE.test(slug) ? slug : '';
}

/**
 * A method row the card can show: an object with a non-empty title or kind.
 * A malformed row (`{}`) rendered the literal "undefined" as an "other
 * ways" eyebrow (2026-09-02 review). Shared by the card and the compact
 * snapshot so the payload rebuild sees exactly the rows the card saw.
 */
function isRenderableMethod(m: unknown): m is ConnectResearchMethod {
  if (!m || typeof m !== 'object') return false;
  return methodEyebrow(m as ConnectResearchMethod).length > 0;
}

/** The model-authored eyebrow for a method row: its title, else its kind. */
function methodEyebrow(m: ConnectResearchMethod): string {
  return (String(m.title || '').trim() || String(m.kind || '').trim()).slice(0, 90);
}

/**
 * THE LABEL RULE (contract C1, 2026-09-02) — the primary pill's label comes
 * from destination truth, never from the research's `recommended` kind (a
 * lane switch would render dead CTAs for lanes the client cannot run):
 *   verifiedKind login-page | bot-walled → "open <app> sign-in"
 *   verifiedKind app-host               → "open <app>"
 *   signInUrl but no/unverified kind    → "open <app>", no extra line
 *   no signInUrl                        → "open Prest0n's computer" + the D9 note
 */
export function connectCardPrimaryLabel(
  appName: string,
  research: ConnectResearch | null | undefined,
  lang: ConnectCardLang,
): { label: string; note: string | null } {
  const url = researchSignInUrl(research);
  if (!url) {
    return {
      label: CONNECT_CARD_COPY.primaryUnverified[lang],
      note: CONNECT_CARD_COPY.unverifiedNote[lang],
    };
  }
  const kind = String(research?.verifiedKind || '');
  if (kind === 'login-page' || kind === 'bot-walled') {
    return { label: CONNECT_CARD_COPY.primarySignIn[lang](appName), note: null };
  }
  return { label: CONNECT_CARD_COPY.primaryApp[lang](appName), note: null };
}

/** The official https link an informational "other ways" row can carry —
 *  the research object already names them (§9.16); none → eyebrow only. */
function officialLinkForMethod(kind: string, research: ConnectResearch | null | undefined): string {
  const pick = kind === 'api-key' ? research?.apiKeyUrl
    : kind === 'desktop-app' ? research?.desktopApp
    : kind === 'mcp' ? research?.mcp
    : '';
  return safeHttpsUrl(pick);
}

function linkHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return url;
  }
}

/**
 * Contract C2 (2026-09-02): the COMPACT research snapshot the card and its
 * payload carry — methods ≤ 4 with pros/cons ≤ 1 each — so the thread-
 * durability rebuild re-renders byte-identical html from the payload alone.
 * Flat verified fields (verifiedKind, signupUrl, …) ride through untouched.
 */
export function compactConnectResearch(r: ConnectResearch | null | undefined): ConnectResearch | null {
  if (!r || typeof r !== 'object') return null;
  const methods = (Array.isArray(r.methods) ? r.methods : [])
    .filter(isRenderableMethod)
    .slice(0, 4)
    .map((m) => ({
      kind: m.kind,
      title: String(m.title || '').slice(0, 90),
      pros: (Array.isArray(m.pros) ? m.pros : []).slice(0, 1).map((s) => String(s).slice(0, 120)),
      cons: (Array.isArray(m.cons) ? m.cons : []).slice(0, 1).map((s) => String(s).slice(0, 120)),
      effort: m.effort,
    }));
  return { ...r, summary: String(r.summary || '').slice(0, 200), methods };
}

/**
 * The browser-lane connect card (rebuilt on actionCardKit 2026-09-02 — plan
 * P0 #7, D1's shape fix, §9.16): ONE recommendation line (the research's
 * recommended method title + its first "why", model-authored), ONE primary
 * pill on the browser lane — the only lane with an execution plane — whose
 * label is destination truth (connectCardPrimaryLabel), the D9 lines that
 * truth needs (unverified page / phone-only limit), and the other methods
 * inside "other ways ▾" as INFORMATIONAL rows with their official links
 * (anchors, never a dead <button>). Never a lane switch. The same builder
 * re-renders a card from its payload snapshot at hydration (thread
 * durability, contract C2) — keep it a pure projection of its inputs.
 *
 * `data-app-name` on the pill is load-bearing: the standing-offer guard's
 * html fallback and the useGlobalTools click handler both read it.
 */
export function buildBrowserLaneCardHtml(
  appName: string,
  research?: ConnectResearch | null,
  opts?: {
    lang?: ConnectCardLang;
    reconnect?: boolean;
    open?: boolean;
    slug?: string;
    /**
     * The CATALOG toolkit slug when this app's catalog entry declares an
     * API_KEY / BASIC / BEARER_TOKEN scheme (2026-09-03). Set, the research's
     * own api-key row stops being purely informational and gains a pill that
     * opens the key dialog. Absent, the card is exactly what it was.
     *
     * Deliberately only ever attached to a row the RESEARCH already chose to
     * show: that row is what tells the user where to GET the key. A bare
     * "paste your key" pill on an app whose research never mentioned one sends
     * people hunting for something they may not have, which is a worse card
     * than the browser sign-in it would be competing with.
     *
     * It rides the payload snapshot too (contract C2) — a rehydrated card that
     * lost the pill would be a card that quietly got worse on reload.
     */
    keyToolkitSlug?: string;
  },
): string {
  const lang: ConnectCardLang = opts?.lang === 'es' ? 'es' : 'en';
  const name = String(appName || '').slice(0, 60);
  const methods = Array.isArray(research?.methods) ? research!.methods.filter(isRenderableMethod) : [];
  const parts: string[] = [];
  // (a) ONE recommendation line — projected verbatim from the research.
  const rec = methods.find((m) => m.kind === research?.recommended);
  if (rec) {
    const title = String(rec.title || rec.kind).slice(0, 90);
    const why = Array.isArray(rec.pros) && rec.pros[0] ? String(rec.pros[0]).slice(0, 120) : '';
    parts.push(`<div class="p0ac-detail">${escapeHtml(title)}${why ? ` — ${escapeHtml(why)}` : ''}</div>`);
  }
  // (b) destination truth → the label, plus the D9 note when there is no page.
  const url = researchSignInUrl(research);
  const primary = connectCardPrimaryLabel(name, research, lang);
  if (primary.note) parts.push(`<div class="p0ac-note">${escapeHtml(primary.note)}</div>`);
  // (c) a phone-only verdict → the one code-authored limit line.
  if (research?.recommended === 'mobile') {
    parts.push(`<div class="p0ac-note">${escapeHtml(CONNECT_CARD_COPY.mobileLimit[lang])}</div>`);
  }
  // (d) the primary pill — same id + data attributes the click handler reads.
  // A reconnect card keeps the id and carries data-reconnect PLUS the
  // roster's own slug (data-custom-slug): the click hands that slug straight
  // to the sign-in surface and registers NOTHING — the register path merges
  // the chat lane's defaults (scope personal, access full) over the stored
  // doc, so a "sign in again" click used to rewrite a read-only or org-
  // scoped connection before any sign-in happened (2026-09-02 review). A
  // rebuild that omits `slug` renders the label but degrades the click to
  // the register path (the server's omitted-field belt then keeps access).
  const label = opts?.reconnect ? CONNECT_CARD_COPY.reconnect[lang](name) : primary.label;
  const reconnectSlug = opts?.reconnect ? customConnectionSlug(opts?.slug) : '';
  const dataAttrs =
    `data-app-name="${escapeHtml(name)}"` +
    (url ? ` data-sign-in-url="${escapeHtml(url)}"` : '') +
    (opts?.reconnect ? ' data-reconnect="true"' : '') +
    (reconnectSlug ? ` data-custom-slug="${escapeHtml(reconnectSlug)}"` : '');
  parts.push(`<div class="p0ac-actions">${renderPillButton({
    id: `${CONNECT_APP_BUTTON_IDS.browser}-open`,
    label,
    variant: 'primary',
    size: 'md',
    dataAttrs,
  })}</div>`);
  // (e) other ways — every non-browser method, informational, at most 4.
  const keySlug = String(opts?.keyToolkitSlug || '').toLowerCase();
  const alternatives = methods
    .filter((m) => m.kind !== 'browser')
    .slice(0, 4)
    .map((m) => {
      const link = officialLinkForMethod(m.kind, research);
      const linkPill = link ? renderLinkPill({ href: link, label: linkHost(link) }) : '';
      // The one row that can DO something: paste the key here. The link pill
      // stays beside it — where to get the key and where to put it are two
      // different questions and the user needs both.
      const actionPill =
        m.kind === 'api-key' && keySlug
          ? renderPillButton({
              id: `${CONNECT_APP_BUTTON_IDS.key}-open`,
              label: CONNECT_CARD_COPY.pasteKey[lang],
              variant: 'ghost',
              size: 'sm',
              dataAttrs:
                `data-app-name="${escapeHtml(name)}" data-toolkit-slug="${escapeHtml(keySlug)}" data-lang="${lang}"`,
            })
          : '';
      const pillHtml = `${actionPill}${linkPill}`;
      return {
        eyebrow: methodEyebrow(m),
        ...(pillHtml ? { pillHtml } : {}),
      };
    });
  parts.push(renderChoiceDisclosure({
    alternatives,
    summaryLabel: CONNECT_CARD_COPY.otherWays[lang],
    // Closed on a proceed card; OPEN when the primary path has no verified
    // destination — the closest P0 analogue of "the primary path just failed".
    open: opts?.open ?? !url,
  }));
  return renderCardShell({
    maxWidth: 480,
    title: CONNECT_CARD_COPY.title[lang],
    bodyHtml: parts.filter(Boolean).join('\n  '),
  });
}

/**
 * Popup-blocked fallback card (2026-08-19, owner report: the chat connect
 * "didn't really confirm in the process"): when the browser refuses the
 * popup even after the engine's retry, chat used to go SILENT — button stuck
 * on "connecting…" until a 2-minute timeout. This card carries the minted
 * OAuth URL on a button; the useGlobalTools handler opens it inside the
 * click gesture with the shared window name, which is never popup-blocked.
 */
export function buildPopupBlockedCardHtml(toolkitSlug: string, appName: string, url: string): string {
  const slug = escapeHtml(toolkitSlug);
  const name = escapeHtml(appName.slice(0, 60));
  const safeUrl = escapeHtml(url);
  const buttonId = `${CONNECT_APP_BUTTON_IDS.reopen}-${slug}`;
  return `<div style="margin-top:16px;border:1px solid ${CONNECT_APP_THEME.border};background:${CONNECT_APP_THEME.accentBg};border-radius:12px;padding:14px 16px;max-width:420px;">
  <div style="font-weight:700;color:${PREST0_INK};font-size:0.95rem;">your browser blocked the sign-in window</div>
  <div style="color:${CONNECT_APP_THEME.subtitle};font-size:0.82rem;margin-top:8px;line-height:1.5;">one tap opens it directly — a direct click is never blocked. everything picks up automatically once you finish signing in.</div>
  <button id="${buttonId}" data-button-id="${buttonId}" data-toolkit-slug="${slug}" data-connect-url="${safeUrl}" style="margin-top:12px;padding:9px 18px;background:${PREST0_INK};color:#fff;border:none;border-radius:999px;font-weight:700;cursor:pointer;font-size:0.82rem;text-transform:lowercase;letter-spacing:0.01em;">open the ${name} sign-in</button>
</div>`;
}

/**
 * Wrong-sign-in remedy card (C3b): [connect <address> instead] — removes the
 * connection that just verified wrong and relaunches with the SAME intended
 * address. The button carries everything the handler needs as data
 * attributes so it survives reloads.
 */
export function buildRetryAsCardHtml(
  toolkitSlug: string,
  appName: string,
  intendedAddress: string,
  connectionId: string | null,
): string {
  const slug = escapeHtml(toolkitSlug);
  const name = escapeHtml(appName.slice(0, 60));
  const address = escapeHtml(intendedAddress.slice(0, 120));
  const buttonId = `${CONNECT_APP_BUTTON_IDS.retryAs}-${slug}`;
  const connAttr = connectionId ? ` data-connection-id="${escapeHtml(connectionId.slice(0, 120))}"` : '';
  return `<div style="margin-top:12px;">
  <button id="${buttonId}" data-button-id="${buttonId}" data-toolkit-slug="${slug}" data-toolkit-name="${name}" data-intended-address="${address}"${connAttr} style="padding:9px 18px;background:${PREST0_INK};color:#fff;border:none;border-radius:999px;font-weight:700;cursor:pointer;font-size:0.82rem;text-transform:lowercase;letter-spacing:0.01em;">connect ${address} instead</button>
</div>`;
}

/**
 * Post-connect folder-scope pointer for storage apps: ONE anchor styled as a
 * button, navigating to the internal (sanitizer-whitelisted) /integrations
 * route, with a data attribute identifying the app. Anchors navigate
 * natively — the delegated click handler only intercepts <button> elements.
 */
export function buildChooseFoldersButtonHtml(toolkitSlug: string): string {
  const slug = escapeHtml(toolkitSlug);
  // ?manage=<slug> (2026-08-14): bare /integrations looked like a dead
  // refresh when clicked from the on-page chat overlay — the deep link lands
  // straight in the app's folder-scope picker / access menu.
  return `<div style="margin-top:12px;"><a href="/integrations?manage=${slug}" data-toolkit-slug="${slug}" style="display:inline-flex;align-items:center;justify-content:center;padding:9px 18px;background:${PREST0_INK};color:#fff;border-radius:999px;font-weight:700;font-size:0.82rem;text-transform:lowercase;letter-spacing:0.01em;text-decoration:none;">manage access &amp; folders</a></div>`;
}

/**
 * Needs-setup pointer (2026-08-24 WhatsApp repro): the OLD needs-setup card
 * reused the storage "manage access & folders" pill, which routed to the
 * ACCESS sheet — for an app with no connection yet, a fully dead sheet. This
 * pill says what it does and ?setup= opens the actual quick setup form.
 */
export function buildFinishSetupButtonHtml(toolkitSlug: string): string {
  const slug = escapeHtml(toolkitSlug);
  return `<div style="margin-top:12px;"><a href="/integrations?setup=${slug}" data-toolkit-slug="${slug}" style="display:inline-flex;align-items:center;justify-content:center;padding:9px 18px;background:${PREST0_INK};color:#fff;border-radius:999px;font-weight:700;font-size:0.82rem;text-transform:lowercase;letter-spacing:0.01em;text-decoration:none;">finish setup</a></div>`;
}

// ============================================================================
// Messaging surface (minimal — usable from the tool step AND the hook)
// ============================================================================

/** The only messaging capability the connect flow needs. */
export type ConnectAppMessaging = Pick<MessagingHelpers, 'addSystemMessage'>;

/**
 * Post the connect offer card. Structured payload carries DATA ONLY; the
 * buttons ride `workflowActionButtonsHtml` so they survive the LLM re-render.
 */
export function postConnectOffer(
  messaging: ConnectAppMessaging,
  apps: ResolvedConnectApp[],
  requestedApp: string
): void {
  messaging.addSystemMessage({
    payload: {
      kind: 'composio.connect_offer',
      toolId: CONNECT_APP_TOOL_ID,
      severity: 'info',
      data: {
        requestedApp,
        apps: apps.map((a) => ({ slug: a.slug, name: a.name, oneClick: a.oneClick })),
      },
      // Machine context only (2026-08-20): no renderer composes from this —
      // the card is data-only and the ORCHESTRATOR's own streamed sentence
      // accompanies it (owner directive: special messages are authored under
      // the primary orchestrator's authority, never a client side-model).
      summary: `composio: connect card offered for ${apps
        .map((a) => `${a.name} (${appPurposePhrase(a.slug)})`)
        .join(', ')}`,
    },
    renderPolicy: 'never',
    sourceTag: 'composio_connect_offer',
    workflowActionButtonsHtml: buildConnectOfferCardHtml(apps),
  });
}

/** Minimal transcript-row shape the standing-offer guard reads — structural
 *  so callers can pass ChatMessage[] without an import cycle. */
export interface ConnectOfferTranscriptMessage {
  sourceTag?: string;
  payload?: { data?: Record<string, unknown> } | null;
  workflowActionButtonsHtml?: string;
  /** true on the user's own rows (ChatMessage.isUser) — the guard scopes
   *  itself to rows AFTER the last one (same-turn duplicate protection). */
  isUser?: boolean;
}

/** Options for the offer helpers (2026-08-20 dedupe): `transcript` enables
 *  the same-turn standing-offer guard; `force` bypasses it (an orchestrator-
 *  decided offer or an explicit user command re-posts a fresh card at the
 *  bottom — never a dead end); `lang` picks the card's {en, es} chrome.
 *  HONEST STATUS (2026-09-02 review): every live lane passes `force` (the
 *  orchestrator lane, the tool step, onUnsupported) or no transcript at all
 *  (composioAction), so in P0 the guard runs for NO production caller — the
 *  per-turn key in useGlobalTools.maybeAutoOfferConnectCard is the live
 *  in-session belt (a mid-turn reload can double-post once; accepted, brief
 *  Q1). The guard stays as P1a's evidence input, pinned by
 *  connectOfferDedupe.test.ts. */
export interface ConnectOfferOptions {
  transcript?: ReadonlyArray<ConnectOfferTranscriptMessage>;
  force?: boolean;
  lang?: ConnectCardLang;
  /** When the user's connect ask began, for hop A of the up-and-go budget
   *  (plan §3.8: 8s on a cache hit, 20s cold). The browser-lane card has
   *  three entry points and the slow part — the catalog fetch — happens
   *  BEFORE two of them, so the outermost caller stamps the start and the
   *  card's funnel row reports the whole wait. Omitted, each entry point
   *  measures from itself, which is honest but shorter. */
  startedAt?: number;
}

/**
 * Does THIS TURN already carry a connect card for this app? (2026-08-20
 * incident: "continue" after an offer turn minted a duplicate card — the
 * only dedupe was an in-memory ref one of the two posting lanes never
 * wrote.) Transcript-derived so it survives reload and covers BOTH lanes;
 * matches by connectAppOfferKey over the card payload's requestedApp/apps,
 * falling back to the persisted button html's data attributes.
 *
 * SAME-TURN ONLY (2026-09-02, plan P0 #4 / D7): only rows AFTER the last
 * user row count. The whole-thread scan blocked every legitimate re-ask
 * ("asked for the button again … never could bring up the tile") while
 * still missing the RingCentral-class duplicate. A card from an EARLIER
 * turn never suppresses — the primary decides whether a second card is
 * warranted; it has the thread. Rows without `isUser` (older fixtures,
 * single-thread mode) count as the current turn.
 *
 * REACHABILITY (2026-09-02 review): no live lane reaches this guard in P0 —
 * every caller passes `force` or omits the transcript (see
 * ConnectOfferOptions). It is not duplicate protection today; it is the
 * turn-scoped belt any non-force caller gets, and P1a's evidence input.
 */
export function threadHasStandingConnectOffer(
  messages: ReadonlyArray<ConnectOfferTranscriptMessage> | undefined,
  appName: string
): boolean {
  const wanted = connectAppOfferKey(appName);
  if (!wanted || !Array.isArray(messages)) return false;
  let turnStart = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.isUser === true) { turnStart = i + 1; break; }
  }
  for (const m of messages.slice(turnStart)) {
    if (!m) continue;
    const tag = m.sourceTag || '';
    if (tag !== 'composio_connect_offer' && tag !== 'composio_connect_unavailable') continue;
    const data = (m.payload && m.payload.data) || {};
    const requested = typeof data.requestedApp === 'string' ? data.requestedApp : '';
    if (requested && connectAppOfferKey(requested) === wanted) return true;
    const apps = Array.isArray(data.apps) ? (data.apps as Array<{ slug?: string; name?: string }>) : [];
    if (apps.some((a) => connectAppOfferKey(a?.slug || '') === wanted || connectAppOfferKey(a?.name || '') === wanted)) {
      return true;
    }
    const html = m.workflowActionButtonsHtml || '';
    if (html) {
      const attrs = html.match(/data-(?:app-name|toolkit-slug|toolkit-name)="([^"]*)"/g) || [];
      if (attrs.some((a: string) => connectAppOfferKey(a.replace(/^[^"]*"|"$/g, '')) === wanted)) return true;
    }
  }
  return false;
}

/**
 * Try to turn a free-form query into a connect offer. Exported for
 * composioAction's zero-connected-apps path so it can delegate here instead
 * of dead-ending to the Integrations page. Returns true if a card was posted.
 */
export async function offerConnectForQuery(
  query: string,
  messaging: ConnectAppMessaging,
  opts?: ConnectOfferOptions
): Promise<boolean> {
  // Hop A starts BEFORE listToolkits() below: a cold catalog fetch is the
  // single biggest thing the user waits through on this path.
  const startedAt = opts?.startedAt ?? Date.now();
  try {
    const triggerMatch = matchConnectAppTrigger(query);
    const appQuery = (triggerMatch.matches && triggerMatch.appQuery) || query;
    // Standing-offer guard (2026-08-20): a card for this app already stands
    // in the thread — treat as posted so the caller's own dedupe key stays
    // set, and post nothing. Checked BEFORE the catalog fetch: a deduped
    // offer costs zero network.
    if (!opts?.force && threadHasStandingConnectOffer(opts?.transcript, appQuery)) {
      return true;
    }
    const toolkits = await listToolkits();
    const apps = resolveRequestedApps(appQuery, toolkits);
    if (apps.length === 0) {
      // Catalog miss (2026-08-19): this helper is what the orchestrator's
      // offer_app_connection rides — it used to return false and post
      // NOTHING here, a silent dead end for exactly the apps the browser
      // lane exists for. The concierge card is the answer for those apps,
      // from every surface.
      // Also lands here (2026-09-02) when the catalog DID match but the app
      // has no one-click sign-in — Google Chat, DocuSign, Xero, Shopify and
      // the ~1,326 others. Name the card from the catalog so it says
      // "Google Chat", not whatever the user typed.
      await offerBrowserLaneForApp(
        browserLaneDisplayName(appQuery, toolkits),
        messaging,
        { toolkits, ...opts, startedAt },
      );
      return true;
    }
    // ROSTER CHECK (2026-08-19): the tool step has always skipped apps whose
    // connection is ACTIVE; this shared helper did not, so an offer arriving
    // through it could invite the user to connect an app they already use.
    // An all-connected resolution degrades to the same already-connected
    // note (+ add-another card) the tool step posts. Best-effort: a roster
    // read failure offers everything (attempt before denying).
    let activeSlugs = new Set<string>();
    try {
      const accounts = await getConnectedAccounts();
      activeSlugs = new Set(
        accounts
          .filter((a) => (a.status || '').toUpperCase() === 'ACTIVE')
          .map((a) => (a.toolkit?.slug || '').toLowerCase())
      );
    } catch {
      // Non-fatal — offer everything we resolved.
    }
    const toOffer = apps.filter((a) => !activeSlugs.has(a.slug.toLowerCase()));
    if (toOffer.length === 0) {
      const already = apps[0];
      messaging.addSystemMessage({
        payload: {
          kind: 'composio.already_connected',
          toolId: CONNECT_APP_TOOL_ID,
          severity: 'info',
          data: { toolkitSlug: already.slug, displayName: already.name, canAddAnother: true },
          summary:
            `composio: ${already.name} is already connected and ready to use — the user can just ask for actions on it. ` +
            `The card below adds ANOTHER ${already.name} account (a second one) if that is what they want. ` +
            `Write ONE short natural sentence saying it is already connected and that they can add another account with the button below. Do NOT introduce yourself.`,
        },
        sourceTag: 'composio_already_connected',
        workflowActionButtonsHtml: buildAddAnotherCardHtml(already),
      });
      return true;
    }
    postConnectOffer(messaging, toOffer, appQuery);
    return true;
  } catch {
    return false;
  }
}

/**
 * Browser-lane CONCIERGE for a system with no catalog toolkit — research
 * first (2026-08-18 owner directive), then the connect_unavailable message
 * whose card runs the browser sign-in lane. Extracted (2026-08-19) so the
 * connect-app tool step AND offerConnectForQuery (the orchestrator's
 * offer_app_connection) post the SAME concierge — one journey from every
 * surface. Research is enhancement,
 * never a gate: any failure falls back to the plain browser-lane offer that
 * always worked.
 */
export async function offerBrowserLaneForApp(
  appQuery: string,
  messaging: ConnectAppMessaging,
  opts?: { toolkits?: ComposioToolkit[] } & ConnectOfferOptions
): Promise<void> {
  // Same-turn standing-offer guard (2026-08-20, scoped to the turn
  // 2026-09-02) — see threadHasStandingConnectOffer. Inert for every live
  // caller in P0 (all pass `force` or no transcript); kept for non-force
  // callers and as P1a's evidence input.
  if (!opts?.force && threadHasStandingConnectOffer(opts?.transcript, appQuery)) {
    return;
  }
  const startedAt = opts?.startedAt ?? Date.now();
  const lang: ConnectCardLang = opts?.lang === 'es' ? 'es' : 'en';
  // Already connected through the browser lane? The REFUSAL is gone (plan
  // §9.4 / P0 #4, 2026-09-02): the primary decided to offer with the whole
  // thread in view (its already-connected prompt rule stands), so the
  // answer is a card whose primary CTA signs in AGAIN — the way a finished
  // sign-in whose session expired gets rescued, and the way "the tile is
  // gone" gets a fresh tile. The roster fact rides the payload
  // (alreadyConnected / slug / signedInAs) instead of suppressing the post.
  // Matching is by connectAppOfferKey (2026-08-20) so a `custom-ring_central`
  // registration is found by a "RingCentral" ask — registration itself still
  // uses customSlugForName verbatim (server-synced). Best-effort: any lookup
  // failure just proceeds to the plain offer (attempt before denying).
  let reconnect: { slug: string; signedInAs?: string } | null = null;
  try {
    const [{ listCustomConnections }] = await Promise.all([
      import('../../composio/ComposioClient'),
    ]);
    const wantedKey = connectAppOfferKey(appQuery);
    const customs = await listCustomConnections();
    const existing = wantedKey
      ? customs.find(
          (c) =>
            connectAppOfferKey(String(c.slug || '').replace(/^custom-/, '')) === wantedKey &&
            String(c.status || '') === 'connected'
        )
      : undefined;
    if (existing) {
      reconnect = {
        slug: String(existing.slug || ''),
        ...(existing.signedInAs ? { signedInAs: String(existing.signedInAs).slice(0, 120) } : {}),
      };
    }
  } catch {
    // roster unknown — proceed to the offer
  }
  let research: ConnectResearch | null = null;
  let researchFull: ConnectResearch | null = null;
  // Funnel split for hop A (plan §3.8): where this card's research came from.
  let researchSource: 'seed' | 'catalog' | 'cache' | 'cold' | 'failed' = 'cold';
  try {
    const researched = await fetchConnectResearch(appQuery);
    const flags = researched as { cached?: boolean; catalog?: boolean; source?: string };
    researchSource = flags.source === 'seed' ? 'seed' : flags.catalog ? 'catalog' : flags.cached ? 'cache' : 'cold';
    researchFull = researched.research ?? null;
    // The card and its payload snapshot ride the COMPACT research (contract
    // C2) — the thread-durability rebuild re-renders from exactly this object.
    research = compactConnectResearch(researchFull);
    if (research?.signInUrl) registerResearchedSignInUrl(appQuery, research.signInUrl);
  } catch {
    // Offline/denied research — the offer below stands on its own.
    researchSource = 'failed';
  }
  let toolkits = opts?.toolkits;
  if (!toolkits) {
    try {
      toolkits = await listToolkits();
    } catch {
      toolkits = [];
    }
  }
  const oneClickSuggestions = toolkits
    .filter((tk) => laneIsDirect(connectLaneFor(tk)))
    .slice(0, 5)
    .map((tk) => tk.name || tk.slug);
  const methodBrief = researchFull
    ? researchFull.methods
        .map((m) => `${m.kind} — ${m.title} (pros: ${m.pros.join(', ') || 'n/a'}; cons: ${m.cons.join(', ') || 'n/a'})`)
        .join('; ')
    : '';
  // Connect-intent warmup (2026-08-31 instant-connect): the card below IS
  // the explicit intent moment — the user asked to connect this app — so
  // the workstation boot kick, VM transport warm, and desktop pre-create
  // all start now, while the user reads the card. The click then reveals a
  // session that already exists (server-side per-user reuse guard).
  void import('../../composio/connectIntentWarmup')
    .then(({ warmUpConnectIntent }) =>
      warmUpConnectIntent({
        appName: appQuery,
        ...(research?.signInUrl ? { signInUrl: research.signInUrl } : {}),
        preCreateSession: true,
      })
    )
    .catch(() => undefined);
  const signInUrl = researchSignInUrl(research);
  const verifiedKind = String(research?.verifiedKind || '');
  // Does the catalog entry behind this card declare a pasted-key scheme
  // (2026-09-03)? Read from the catalog copy already in hand — no round trip
  // on the card's own path. A cold catalog or no match simply answers no, and
  // the card is what it has always been.
  const keyToolkitSlug =
    browserLaneMatches(appQuery, toolkits).filter(toolkitDeclaresKeyScheme)[0]?.slug || '';
  messaging.addSystemMessage({
    payload: {
      kind: 'composio.connect_unavailable',
      toolId: CONNECT_APP_TOOL_ID,
      severity: 'info',
      // Payload snapshot (contract C2, 2026-09-02): everything the card was
      // built from, so a message whose card html was dropped under the
      // thread-doc byte budget re-renders from here at hydration.
      data: {
        requestedApp: appQuery,
        appKey: connectAppOfferKey(appQuery),
        catalogSize: toolkits.length,
        oneClickSuggestions,
        browserLaneOffered: true,
        ...(research ? { research } : {}),
        ...(signInUrl ? { signInUrl } : {}),
        ...(verifiedKind ? { verifiedKind } : {}),
        ...(keyToolkitSlug ? { keyToolkitSlug } : {}),
        lang,
        ...(reconnect
          ? {
              alreadyConnected: true,
              slug: reconnect.slug,
              ...(reconnect.signedInAs ? { signedInAs: reconnect.signedInAs } : {}),
            }
          : {}),
        cardVersion: 2,
      },
      // Machine context only (2026-08-20): the researched verdict renders
      // DETERMINISTICALLY on the card itself (buildBrowserLaneCardHtml) —
      // no renderer model composes prose here; the ORCHESTRATOR's own
      // streamed sentence accompanies the card.
      summary: reconnect
        ? `composio: "${appQuery}" is connected through Prest0n's browser${reconnect.signedInAs ? ` (signed in as ${reconnect.signedInAs})` : ''}; the card below signs in again (use it if the session expired) — say so in one sentence, do not describe the card`
        : research
          ? `composio: "${appQuery}" has no one-click integration; researched verdict on the card — recommended: "${research.recommended}"; methods: ${methodBrief}`
          : `composio: "${appQuery}" has no one-click integration; browser sign-in card offered`,
    },
    renderPolicy: 'never',
    sourceTag: 'composio_connect_unavailable',
    workflowActionButtonsHtml: buildBrowserLaneCardHtml(appQuery, research, {
      lang,
      reconnect: !!reconnect,
      slug: reconnect?.slug,
      ...(keyToolkitSlug ? { keyToolkitSlug } : {}),
    }),
  });
  // Hop A of the up-and-go budget (plan §3.8): the card posted, split by
  // where its research came from — the funnel sink's first production row.
  logConnectFunnel({
    event: 'strategy_card_rendered',
    toolkitSlug: connectAppOfferKey(appQuery),
    surface: 'chat',
    detail: researchSource,
    // Hop A's own number. It rode `ms: null` from the day the sink shipped,
    // so the tightest budget in the plan was the one figure nobody could
    // check — the 411ms warm surface and the 1m46s card→connected journey
    // were both measured while this stayed a guess.
    ms: Date.now() - startedAt,
  });
}

// ============================================================================
// Click-time connect flow (mint → popup → poll)
// ============================================================================

export type ConnectLaunchOutcome =
  | 'connected'
  | 'timeout'
  | 'denied'
  | 'error'
  // No one-click sign-in — the flow posted the browser-lane card instead
  // (2026-09-02). A resolved outcome, never a failure.
  | 'unsupported';

/**
 * Resume handed to the in-chat setup dialog (2026-08-25 adversarial review):
 * the dialog collects the initiation values and calls this INSIDE its submit
 * click (CustomEvent dispatch is synchronous, so the OAuth popup still opens
 * within the user gesture). It re-enters launchConnectFlow with the SAME
 * chat messaging closure — success narrates composio.connection_complete
 * exactly like a direct card click; the dialog only manages its own UI from
 * the returned outcome ('needs-setup' comes back instead of re-dispatching,
 * so the open dialog refreshes rather than duplicating itself).
 */
export type ConnectSetupResume = (
  values: Record<string, string>,
  signal?: AbortSignal,
  onPopupBlocked?: () => void,
) => Promise<ConnectLaunchOutcome | 'needs-setup'>;

/**
 * The click handler flow (§7.3 steps 3–5): a thin CHAT ADAPTER over the
 * shared connect-popup engine (src/services/composio/connectPopupFlow.ts,
 * 2026-08-18 — the popup/mint/poll mechanics live there, shared with the
 * Integrations page and onboarding). This adapter owns ONLY the structured
 * chat events; their kinds, data shapes, and summaries are contract:
 *  - emit composio.connection_complete on success (plus a "choose folders"
 *    /integrations pointer for storage toolkits),
 *  - on a 403 (server enforces integrations.manage) emit a structured
 *    warning with the server's message + remediation data. No retry loop.
 *
 * Called from useGlobalTools.handleButtonClick (button path) and from the
 * tool's own connect step (orchestrator-direct path).
 */
export async function launchConnectFlow(options: {
  toolkitSlug: string;
  displayName?: string;
  messaging: ConnectAppMessaging;
  signal?: AbortSignal;
  /** C3b email journey: the inbox the user said they are connecting — rides
   *  the coach ("sign in as X") and the post-connect verification probe. */
  intent?: { intendedAddress?: string | null };
  /** Resume from the in-chat setup dialog (2026-08-25): the collected
   *  initiation values ride the mint; needs-setup and popup-blocked route
   *  back to the OPEN dialog (which covers the chat, so a chat-only card
   *  would be invisible) instead of dispatching/posting again. */
  setupResume?: { initFields: Record<string, string>; onPopupBlocked?: () => void };
}): Promise<ConnectLaunchOutcome | 'needs-setup'> {
  const { toolkitSlug, messaging, signal } = options;
  const displayName = options.displayName || friendlyToolkitName(toolkitSlug);
  const intendedAddress = options.intent?.intendedAddress || null;
  // The minted authorize URL — when it is a DIRECT Microsoft authorize URL,
  // the no-mailbox remedy can carry the tenant admin-consent link (an opaque
  // Composio hop degrades to instructions-only, the documented fallback).
  let mintedRedirectUrl: string | null = null;

  const outcome = await runConnectPopupFlow({
    toolkitSlug,
    // The global coach host names the app from this (2026-08-20: chat gets
    // the same "the window will say Composio" coach as the other surfaces).
    displayName,
    surface: options.setupResume ? 'chat-setup' : 'chat',
    signal,
    ...(options.setupResume ? { initFields: options.setupResume.initFields } : {}),
    ...(intendedAddress ? { intent: { intendedAddress } } : {}),
    callbacks: {
      onPopupOpened: ({ redirectUrl }) => {
        mintedRedirectUrl = redirectUrl;
      },
      // C3b: who ACTUALLY signed in, verified moments after ACTIVE. Three
      // designed outcomes, each a data-only structured event the
      // orchestrator narrates (house authorship doctrine); mismatch and
      // no-mailbox carry the one-tap [connect X instead] remedy.
      onVerified: (verification, connectionId) => {
        const base = {
          toolkitSlug,
          displayName,
          identity: verification.identity,
          intendedAddress: verification.intendedAddress || intendedAddress,
          possibleAlias: verification.possibleAlias === true,
        };
        if (verification.verdict === 'confirmed' && verification.identity) {
          // ONE MOMENT, ONE MESSAGE (owner redundancy directive 2026-08-29):
          // the connection_complete message above already told the user the
          // app is connected — a second rendered bubble whose only new fact
          // is the signed-in address restated it (the old summary even
          // hedged "if you have not already confirmed…"). The confirmed
          // identity now lands as a SILENT data-only record: the model's
          // context learns who is signed in (so "which account is this?"
          // answers correctly next turn) with no second bubble. The
          // actionable verdicts below — mismatch and no-mailbox — still
          // render, because they carry a remedy the user must see.
          messaging.addSystemMessage({
            renderPolicy: 'never',
            payload: {
              kind: 'composio.connection_verified',
              toolId: CONNECT_APP_TOOL_ID,
              severity: 'success',
              data: base,
              summary: `composio: verified — this ${displayName} connection is signed in as ${verification.identity}${base.intendedAddress ? ' (the inbox the user asked for)' : ''}. The connection card already confirmed the connect: say NOTHING now, and mention the signed-in address only when the user asks or it matters to what they are doing.`,
            },
            sourceTag: 'composio_connection_verified',
          });
          return;
        }
        if (verification.verdict === 'mismatch' && base.intendedAddress) {
          messaging.addSystemMessage({
            payload: {
              kind: 'composio.connection_identity_mismatch',
              toolId: CONNECT_APP_TOOL_ID,
              severity: 'warning',
              data: { ...base, connectionId: connectionId || null },
              summary: `composio: the ${displayName} sign-in that just completed is ${verification.identity || 'a different account'} — the user meant ${base.intendedAddress}.${base.possibleAlias ? ' The domains match, so it may be an alias of the same inbox — keeping it can be fine.' : ''} Tell the user plainly which account actually signed in, and that the button below removes this sign-in and reconnects as the address they meant (or they can keep it if it is right). Do not introduce yourself.`,
            },
            sourceTag: 'composio_connection_identity_mismatch',
            workflowActionButtonsHtml: buildRetryAsCardHtml(toolkitSlug, displayName, base.intendedAddress, connectionId || null),
          });
          return;
        }
        if (verification.verdict === 'no-mailbox') {
          const adminConsentUrl = deriveMicrosoftAdminConsentUrl(mintedRedirectUrl);
          messaging.addSystemMessage({
            payload: {
              kind: 'composio.connection_no_mailbox',
              toolId: CONNECT_APP_TOOL_ID,
              severity: 'warning',
              data: { ...base, connectionId: connectionId || null, adminConsentUrl },
              summary: `composio: the ${displayName} account that signed in${verification.identity ? ` (${verification.identity})` : ''} has NO usable mailbox — usually an admin or unlicensed account signed in instead of the inbox itself. Email actions will fail until this is fixed. Tell the user: reconnect signed in as the inbox itself${base.intendedAddress ? ` (${base.intendedAddress} — the button below does it)` : ''}, or ask their admin to give that account a mailbox license. If an admin needs to approve the app, the admin approves it from their admin console${adminConsentUrl ? ' (data.adminConsentUrl is that approval link — share it as a clickable link)' : ''} — the admin must NEVER sign in with their own account. Do not introduce yourself.`,
            },
            sourceTag: 'composio_connection_no_mailbox',
            workflowActionButtonsHtml: base.intendedAddress
              ? buildRetryAsCardHtml(toolkitSlug, displayName, base.intendedAddress, connectionId || null)
              : undefined,
          });
        }
        // 'unverified' stays silent — a failed probe must never paint doubt
        // over a working connection.
      },
      // Popup blocked even after the engine's retry (2026-08-19): say so IN
      // CHAT with a one-tap reopen — the old behavior was total silence
      // until the 2-minute timeout. The poll keeps running underneath, so
      // the reopened window lands in the exact same completion path.
      onPopupBlocked: ({ url }) => {
        // Setup-dialog resume (2026-08-25): the dialog COVERS the chat, so
        // the reopen card below would be invisible — and the dialog aborts
        // this attempt on block (a fresh submit is a fresh gesture), which
        // would leave the card's reopen link polling a dead attempt. Route
        // the block to the dialog instead.
        if (options.setupResume) {
          options.setupResume.onPopupBlocked?.();
          return;
        }
        messaging.addSystemMessage({
          payload: {
            kind: 'composio.connect_popup_blocked',
            toolId: CONNECT_APP_TOOL_ID,
            severity: 'info',
            data: { toolkitSlug, displayName },
            summary: `composio: the browser blocked the ${displayName} sign-in window — tell the user one tap on the button below opens it directly (a direct click is never blocked), and everything continues automatically once they sign in. Do not show any raw error text.`,
          },
          sourceTag: 'composio_connect_popup_blocked',
          workflowActionButtonsHtml: buildPopupBlockedCardHtml(toolkitSlug, displayName, url),
        });
      },
      onDenied: (serverMessage) => {
        // §7.3 step 5: server enforces integrations.manage. Surface the
        // server's message with remediation data (renderer composes the
        // "ask an owner or admin to connect this on the Integrations page"
        // prose). No retry loop.
        messaging.addSystemMessage({
          payload: {
            kind: 'composio.connect_permission_denied',
            toolId: CONNECT_APP_TOOL_ID,
            severity: 'warning',
            data: {
              toolkitSlug,
              displayName,
              serverMessage,
              requiredPermission: 'integrations.manage',
              remediation: 'ask_owner_or_admin_to_connect_on_integrations_page',
              integrationsPagePath: '/integrations',
            },
            summary: `composio: connecting ${displayName} was denied by the server (integrations.manage required) — the user should ask an owner or admin to connect it on the Integrations page`,
          },
          sourceTag: 'composio_connect_permission_denied',
        });
      },
      // Setup-fields case (2026-08-14, WhatsApp WABA incident): the app needs
      // extra values before OAuth. The server's 422 carries the toolkit's own
      // field metadata — relay WHAT is needed and where to find it (Composio's
      // descriptions say exactly, e.g. "Find it in Meta Business Suite under
      // Settings → Accounts"), and point at the Integrations page, which has
      // the quick setup form. Never a raw error.
      onNeedsSetup: (errFields, help) => {
        // Setup-dialog resume (2026-08-25): the dialog is ALREADY open — a
        // second dispatch would reset the user's typed values and a second
        // chat message would duplicate the first. The 'needs-setup' outcome
        // returns to the dialog, which refreshes its own fields.
        if (options.setupResume) return;
        // Open the quick setup form RIGHT HERE (2026-08-24 WhatsApp repro):
        // the app-wide ConnectSetupHost listens for this and pops the same
        // SetupFieldsDialog the Integrations page uses — the user fills the
        // value and the connect proceeds without leaving chat. The standing
        // card below (finish-setup pill) is the durable fallback for a
        // dismissed dialog or a reloaded thread. The resume closure carries
        // THIS flow's chat messaging, so a dialog-driven success narrates
        // composio.connection_complete exactly like a direct card click.
        try {
          const resume: ConnectSetupResume = (values, resumeSignal, onBlocked) =>
            launchConnectFlow({
              ...options,
              signal: resumeSignal,
              setupResume: { initFields: values, onPopupBlocked: onBlocked },
            });
          document.dispatchEvent(
            new CustomEvent('prest0n:connect-setup', {
              detail: { toolkitSlug, displayName, resume },
            })
          );
        } catch { /* non-DOM environments: the pill remains the path */ }
        messaging.addSystemMessage({
          payload: {
            kind: 'composio.connect_needs_setup',
            toolId: CONNECT_APP_TOOL_ID,
            severity: 'info',
            data: {
              toolkitSlug,
              displayName,
              fields: errFields.map((f: any) => ({
                displayName: String(f?.displayName || f?.name || ''),
                description: String(f?.description || ''),
                // Enriched help (2026-08-17 WhatsApp journey): deep link to the
                // page where the value lives + what to do when the user does
                // not have the value at all. Empty for un-enriched fields.
                // (No apostrophes in comments inside emit-call args — the
                // forbid-hardcoded-user-strings scanner has no comment
                // handling and reads them as string openers.)
                helpUrl: String(f?.helpUrl || ''),
                notFoundHint: String(f?.notFoundHint || ''),
              })),
              // Account-type triage (2026-08-17): pathway chooser data, so chat
              // can ask which situation fits before prescribing steps.
              help: help || null,
              integrationsPagePath: '/integrations',
            },
            summary:
              `composio: ${displayName} needs one or two setup values before its sign-in can start (${errFields
                .map((f: any) => f?.displayName || f?.name)
                .join(', ')}). A quick setup form for it is opening right now; the finish-setup button below reopens it anytime. If data.help is present, FIRST ask the user which of the help.pathways situations fits them (short question from the labels — many users do not know which account type they have), then give ONLY the chosen pathway's guidance. Otherwise tell the user plainly what is needed and where to find it (use the field descriptions in data; when a field carries a helpUrl, give it as a clickable link — it opens the exact page; relay a notFoundHint when present). Do NOT show any raw error text, never describe the provider's sign-in screens from memory, and do not introduce yourself.`,
          },
          sourceTag: 'composio_connect_needs_setup',
          workflowActionButtonsHtml: buildFinishSetupButtonHtml(toolkitSlug),
        });
      },
      // No one-click sign-in for this app (2026-09-02). Reached from a card
      // that predates the resolver filter, or from a click made while the
      // client's catalog copy was cold. Not an error state: post the
      // researched browser-lane card instead — the SAME concierge card the
      // resolver now routes these apps to before a button is ever offered.
      onUnsupported: ({ displayName: appLabel }) => {
        void (async () => {
          try {
            await offerBrowserLaneForApp(appLabel, messaging, { force: true });
          } catch {
            /* the offer helper is best-effort; the engine already logged */
          }
        })();
      },
      onError: (serverMessage) => {
        messaging.addSystemMessage({
          payload: {
            kind: 'composio.connect_error',
            toolId: CONNECT_APP_TOOL_ID,
            severity: 'error',
            data: { toolkitSlug, displayName, errorMessage: serverMessage },
            summary: `composio: starting the ${displayName} connection failed — ${serverMessage}`,
          },
          sourceTag: 'composio_connect_error',
        });
      },
      onActive: ({ isStorage: storage }) => {
        // The engine already closed the popup, invalidated the connected-apps
        // cache, and fired the capability reconcile (owner directive
        // 2026-08-11: the connection is usable by the assistant IMMEDIATELY).
        messaging.addSystemMessage({
          payload: {
            kind: 'composio.connection_complete',
            toolId: CONNECT_APP_TOOL_ID,
            severity: 'success',
            data: {
              toolkitSlug,
              displayName,
              isStorageToolkit: storage,
              defaultAccessTier: 'full',
              integrationsPagePath: '/integrations',
              // The wow moment (2026-08-18): one concrete thing to try right
              // now — the same suggestion the Integrations page done card
              // shows. (No apostrophes in comments inside emit-call args —
              // the forbid-hardcoded-user-strings scanner has no comment
              // handling and reads them as string openers.)
              suggestedFirstAction: firstActionForApp(toolkitSlug, displayName),
            },
            summary: `composio: ${displayName} is now connected and ready to use — Prest0n has full access by default; tell the user the access level (and folder choices for storage apps) can be changed anytime under Access on the Integrations page${
              storage
                ? '; it is a storage app, so the manage-access-and-folders link below opens those settings'
                : ''
            }. End by inviting ONE concrete first step in the user's own words — offer data.suggestedFirstAction as something they can just ask for right now.`,
          },
          sourceTag: 'composio_connection_complete',
          workflowActionButtonsHtml: storage
            ? buildChooseFoldersButtonHtml(toolkitSlug)
            : undefined,
        });
      },
      onTimeout: () => {
        // C4a: an email-journey timeout WITH a stated inbox is very often the
        // admin-approval wait (days, not minutes) — record it durably so the
        // Integrations page shows an amber waiting tile that survives this
        // tab; the server self-heals it once the connection reports ACTIVE.
        // Fire-and-forget: a failed record only loses the tile.
        const adminWaitLikely = isEmailJourneySlug(toolkitSlug) && Boolean(intendedAddress);
        if (adminWaitLikely) {
          void import('../../composio/ComposioClient')
            .then((m) => m.recordPendingConnection(toolkitSlug, displayName, intendedAddress))
            .catch(() => { /* the amber tile is an enhancement, never the flow */ });
        }
        // Firm-wide-approval handoff: turn the wall into one admin action —
        // a ready-to-send note (and, when the minted URL was a direct
        // Microsoft authorize URL, the one-click tenant approval link).
        const provider = providerForEmailToolkitSlug(toolkitSlug);
        const adminConsentUrl = provider === 'microsoft'
          ? deriveMicrosoftAdminConsentUrl(mintedRedirectUrl)
          : null;
        const adminNote = adminWaitLikely && provider
          ? buildAdminHandoffNote({
              provider,
              appDisplayName: 'Prest0n',
              adminConsentUrl,
              googleClientId: provider === 'google' ? deriveGoogleOAuthClientId(mintedRedirectUrl) : null,
              // The managed app's approval screens name the connection
              // partner, not prest0n — the note must match what the admin
              // will actually see there.
              partnerAppName: 'Composio',
              intendedAddress,
            })
          : null;
        messaging.addSystemMessage({
          payload: {
            kind: 'composio.connection_timeout',
            toolId: CONNECT_APP_TOOL_ID,
            severity: 'info',
            data: {
              toolkitSlug,
              displayName,
              waitedSeconds: Math.round((CONNECT_POLL_INTERVAL_MS * CONNECT_POLL_MAX_TICKS) / 1000),
              ...(adminWaitLikely ? { pendingAdminRecorded: true, intendedAddress } : {}),
              ...(adminNote ? { adminNote, ...(adminConsentUrl ? { adminConsentUrl } : {}) } : {}),
            },
            summary: adminWaitLikely
              ? `composio: no active ${displayName} connection observed after polling. If an admin has to approve the app first, that can take a while — tell the user this wait is SAVED: the Integrations page now shows ${displayName} as waiting on approval, and once the admin approves they can finish from there (or just ask here again). Remind them the admin approves the app from their admin console and must never sign in with their own account. data.adminNote is a ready-to-send message for their admin — offer it to the user VERBATIM in a copyable block (one admin action approves the app for their whole firm${adminConsentUrl ? '; the link inside makes it one click' : ''}). Otherwise the sign-in window may simply still be open, or they can click connect again.`
              : `composio: no active ${displayName} connection observed after polling — the OAuth popup may still be open, or the user can click the connect button again`,
          },
          sourceTag: 'composio_connection_timeout',
        });
      },
    },
  });

  // Preserve the historical outcome vocabulary: needs-setup surfaces as
  // 'error' (the setup card IS the user-facing terminal), an abort resolves
  // quietly as 'timeout' with no message — both exactly as before. The
  // setup-dialog resume is the one caller that needs 'needs-setup' raw
  // (its open form refreshes fields on it).
  switch (outcome) {
    case 'connected': return 'connected';
    case 'denied': return 'denied';
    case 'timeout': return 'timeout';
    case 'aborted': return 'timeout';
    case 'needs-setup': return options.setupResume ? 'needs-setup' : 'error';
    // The browser-lane card IS the user-facing terminal here (2026-09-02) —
    // reporting 'error' would make callers post a failure over a working
    // hand-off, which is the whole defect this change removes.
    case 'unsupported': return 'unsupported';
    default: return 'error';
  }
}

// ============================================================================
// State
// ============================================================================

export interface ConnectAppState {
  isResolving: boolean;
}

// ============================================================================
// Tool Definition
// ============================================================================

export const connectAppTool: GlobalToolDefinition<ConnectAppState> = {
  id: CONNECT_APP_TOOL_ID,
  name: 'Connect App',
  description:
    'Offer a one-click in-chat connection card for a third-party app (Gmail, Google Drive, Slack, etc.) that is not connected yet',

  triggerPattern: matchConnectAppTrigger,

  createInitialState: (): ConnectAppState => ({
    isResolving: false,
  }),

  steps: [
    // ========================================================================
    // Step 1: Resolve — match the requested app against the Composio catalog
    // and post the connect card (or an informative structured event).
    // ========================================================================
    {
      id: 'resolve',
      name: 'Resolve App',
      activeDisplayName: 'looking up the app catalog',

      execute: async (
        ctx: StepContext<ConnectAppState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as
          | {
              query?: string;
              appName?: string;
              appQuery?: string;
              toolkitSlug?: string;
            }
          | undefined;

        // Hop A for the tool lane: the user has been waiting since here.
        const hopAStartedAt = Date.now();
        const userQuery = input?.query || ctx.triggerMessage.text || '';
        const triggerMatch = matchConnectAppTrigger(userQuery);
        const appQuery =
          input?.appName ||
          input?.appQuery ||
          input?.toolkitSlug ||
          (triggerMatch.matches && triggerMatch.appQuery) ||
          userQuery;

        ctx.setState({ isResolving: true });

        // Exactly-one-indicator contract (INCIDENT-20260812): every tool
        // keeps a visible pending affordance across its network waits — this
        // step's first output can be seconds away (catalog fetch on a cold
        // tab + an uncached connected-accounts round trip), and connect-app
        // was the ONE registry tool that never called showPending. Each
        // terminal below posts its message first, then retires this handle
        // (zero-gap); the overlay render lane collapses any momentary
        // coexistence with the turn's own widget to one on screen.
        const pending = ctx.messaging.showPending('looking up the app catalog');

        try {
          const toolkits = await listToolkits();

          // Prefer an exact toolkitSlug when the orchestrator supplied one.
          let apps: ResolvedConnectApp[] = [];
          if (input?.toolkitSlug) {
            const direct = toolkits.find(
              (tk) => (tk.slug || '').toLowerCase() === input.toolkitSlug!.toLowerCase()
            );
            // Lane-checked (2026-09-02): an explicit slug from the
            // orchestrator is still only connectable if the app has managed
            // auth. A non-direct one falls through to the browser-lane card
            // below instead of minting a button that cannot work.
            if (direct && laneIsDirect(connectLaneFor(direct))) {
              apps = [toResolvedApp(direct)];
            }
          }
          if (apps.length === 0) {
            apps = resolveRequestedApps(appQuery, toolkits);
          }

          if (apps.length === 0) {
            // Catalog miss — CONCIERGE, research first (2026-08-18 owner
            // directive). The shared offerBrowserLaneForApp helper posts the
            // researched verdict + browser-lane card — the SAME concierge
            // offerConnectForQuery serves the orchestrator's
            // offer_app_connection and the APP_NOT_CONNECTED auto-offer
            // (2026-08-19: one journey from every surface).
            pending.update({
              html: getPendingWidgetHtml('researching the best way to connect'),
            });
            // force: an explicit user command re-posts a fresh card at the
            // bottom of the thread — never a dead end behind an old card.
            // lang: the card's {en, es} chrome follows the user's own ask
            // (2026-09-02 review — this lane rendered English for Spanish).
            await offerBrowserLaneForApp(
              browserLaneDisplayName(appQuery, toolkits),
              ctx.messaging,
              {
                toolkits,
                force: true,
                lang: detectInputLanguage(ctx.triggerMessage?.text || ''),
                startedAt: hopAStartedAt,
              },
            );
            pending.abort();
            ctx.setState({ isResolving: false });
            return { status: 'completed', nextStep: null };
          }

          // Skip apps that are already connected (ACTIVE). Spinner status is
          // html-only (widget label) — a literal `text:` here trips the
          // forbid-hardcoded-user-strings gate (deepResearch's stream.update
          // is the sibling idiom).
          pending.update({
            html: getPendingWidgetHtml('checking connected accounts'),
          });
          let activeSlugs = new Set<string>();
          try {
            const accounts = await getConnectedAccounts();
            activeSlugs = new Set(
              accounts
                .filter((a) => (a.status || '').toUpperCase() === 'ACTIVE')
                .map((a) => (a.toolkit?.slug || '').toLowerCase())
            );
          } catch {
            // Non-fatal — offer everything we resolved.
          }

          const toOffer = apps.filter((a) => !activeSlugs.has(a.slug.toLowerCase()));
          if (toOffer.length === 0) {
            // Already connected — offer ADDING ANOTHER account right here
            // (2026-08-12 owner directive): a second calendar, a second
            // Drive, another mailbox. The button runs the same connect flow;
            // each OAuth run mints its own connection, and multi-account
            // execution/scoping already handles the rest.
            const already = apps[0];
            ctx.messaging.addSystemMessage({
              payload: {
                kind: 'composio.already_connected',
                toolId: CONNECT_APP_TOOL_ID,
                severity: 'info',
                data: { toolkitSlug: already.slug, displayName: already.name, canAddAnother: true },
                summary:
                  `composio: ${already.name} is already connected and ready to use — the user can just ask for actions on it. ` +
                  `The card below adds ANOTHER ${already.name} account (a second one) if that is what they want. ` +
                  `Write ONE short natural sentence saying it is already connected and that they can add another account with the button below. Do NOT introduce yourself.`,
              },
              sourceTag: 'composio_already_connected',
              workflowActionButtonsHtml: buildAddAnotherCardHtml(already),
            });
            pending.abort();
            ctx.setState({ isResolving: false });
            return { status: 'completed', nextStep: null };
          }

          postConnectOffer(ctx.messaging, toOffer, appQuery);
          pending.abort();
          ctx.setState({ isResolving: false });
          return { status: 'completed', nextStep: null };
        } catch (error) {
          ctx.setState({ isResolving: false });
          const errorMessage = error instanceof Error ? error.message : String(error);
          ctx.messaging.addSystemMessage({
            payload: {
              kind: 'composio.connect_error',
              toolId: CONNECT_APP_TOOL_ID,
              severity: 'error',
              data: { requestedApp: appQuery, errorMessage },
              summary: `composio: could not load the app catalog to offer a connection — ${errorMessage}`,
            },
            sourceTag: 'composio_connect_error',
          });
          pending.abort();
          return {
            status: 'failed',
            error: new Error(errorMessage),
            userFacingErrorShown: true,
          };
        }
      },

      cleanup: async (ctx: StepContext<ConnectAppState>) => {
        ctx.setState({ isResolving: false });
      },
    },
  ],

  isAuthorized: isAgenticWorkflowsAuthorized,

  theme: CONNECT_APP_THEME,

  buttonIds: CONNECT_APP_BUTTON_IDS,

  // Buttons never (re)start this tool — clicks are handled inline in
  // useGlobalTools.handleButtonClick via launchConnectFlow.
  canTriggerFromButton: false,
};
